import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SolutionStoryOption,
  SolutionStoryOptionCategory,
  solutionStoriesApi,
} from '../../services/api';
import { STORY_UI } from './storyUiTokens';
import { SOLUTION_STORY_OPTIONS_QUERY_KEY } from './useSolutionStoryOptions';

const ADMIN_OPTIONS_QUERY_KEY = ['solution-story-admin-options'] as const;

// Google Slides' replaceImage rejects images over 25 megapixels regardless of file size in
// bytes (a small PNG can still be huge in pixel dimensions). Capping the upload size here is a
// simple proxy that catches the oversized exports that actually trip that limit in practice.
const MAX_ICON_FILE_SIZE_BYTES = 2 * 1024 * 1024;

const CATEGORY_LABELS: Record<SolutionStoryOptionCategory, string> = {
  practice: 'Practice',
  industry: 'Industry',
  platform: 'Platform',
  techstack: 'Tech Stack',
  company: 'Company Logos',
};

const CATEGORY_ORDER: SolutionStoryOptionCategory[] = ['practice', 'industry', 'platform', 'techstack', 'company'];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    fontFamily: STORY_UI.fontSans,
    backgroundColor: '#fff',
  },
  '& .MuiInputLabel-root': { fontSize: '13px' },
};

type RowProps = {
  option: SolutionStoryOption;
  isFirst: boolean;
  isLast: boolean;
  onChanged: () => void;
};

const OptionRow: React.FC<RowProps> = ({ option, isFirst, isLast, onChanged }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(option.value);
  const [label, setLabel] = useState(option.label ?? '');
  const [error, setError] = useState<string | null>(null);

  const dirty = value.trim() !== option.value || (label.trim() || '') !== (option.label ?? '');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ADMIN_OPTIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY });
    onChanged();
  };

  const handleError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail || err?.message || fallback;
    setError(typeof detail === 'string' ? detail : fallback);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      solutionStoriesApi.updateOption(option.id, {
        value: value.trim(),
        label: option.category === 'industry' ? label.trim() || null : undefined,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => handleError(err, 'Failed to save option'),
  });

  const activeMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      solutionStoriesApi.updateOption(option.id, { is_active: isActive }),
    onSuccess: invalidate,
    onError: (err) => handleError(err, 'Failed to update option'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => solutionStoriesApi.deleteOption(option.id),
    onSuccess: invalidate,
    onError: (err) => handleError(err, 'Failed to delete option'),
  });

  const moveMutation = useMutation({
    mutationFn: (direction: -1 | 1) =>
      solutionStoriesApi.reorderOptions([
        { id: option.id, sort_order: option.sort_order + direction },
      ]),
    onSuccess: invalidate,
    onError: (err) => handleError(err, 'Failed to reorder'),
  });

  const iconMutation = useMutation({
    mutationFn: (file: File) => solutionStoriesApi.uploadOptionIcon(option.id, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => handleError(err, 'Failed to upload icon'),
  });

  const busy =
    saveMutation.isPending ||
    activeMutation.isPending ||
    deleteMutation.isPending ||
    moveMutation.isPending ||
    iconMutation.isPending;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.25,
        borderRadius: STORY_UI.radiusSm,
        border: `1px solid ${STORY_UI.line}`,
        backgroundColor: option.is_active ? '#fff' : '#fafafa',
        opacity: option.is_active ? 1 : 0.7,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1,
          border: `1px solid ${STORY_UI.line}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        {option.icon_url ? (
          <img
            src={option.icon_url}
            alt={option.value}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <ImageIcon sx={{ color: STORY_UI.inkFaint, fontSize: 20 }} />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
            disabled={busy}
          />
          {option.category === 'industry' && (
            <TextField
              size="small"
              label="Short label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ ...fieldSx, width: 140 }}
              disabled={busy}
            />
          )}
        </Box>
        {!option.is_active && (
          <Chip label="Inactive" size="small" sx={{ width: 'fit-content', fontSize: '11px' }} />
        )}
        {error && (
          <Typography sx={{ fontSize: '12px', color: '#c00000' }}>{error}</Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (file.size > MAX_ICON_FILE_SIZE_BYTES) {
                setError(
                  `Icon is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Max size is ${MAX_ICON_FILE_SIZE_BYTES / (1024 * 1024)}MB.`
                );
              } else {
                iconMutation.mutate(file);
              }
            }
            e.target.value = '';
          }}
        />
        <Tooltip title="Save changes">
          <span>
            <IconButton
              size="small"
              disabled={!dirty || busy}
              onClick={() => saveMutation.mutate()}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Upload icon (max ${MAX_ICON_FILE_SIZE_BYTES / (1024 * 1024)}MB)`}>
          <span>
            <IconButton size="small" disabled={busy} onClick={() => fileInputRef.current?.click()}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move up">
          <span>
            <IconButton size="small" disabled={isFirst || busy} onClick={() => moveMutation.mutate(-1)}>
              <UpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton size="small" disabled={isLast || busy} onClick={() => moveMutation.mutate(1)}>
              <DownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {option.is_active ? (
          <Tooltip title="Deactivate">
            <span>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => deleteMutation.mutate()}
                sx={{ color: '#c00000' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Reactivate">
            <span>
              <IconButton size="small" disabled={busy} onClick={() => activeMutation.mutate(true)}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

type AddRowProps = {
  category: SolutionStoryOptionCategory;
  onAdded: () => void;
};

const AddOptionRow: React.FC<AddRowProps> = ({ category, onAdded }) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      solutionStoriesApi.createOption({
        category,
        value: value.trim(),
        label: category === 'industry' ? label.trim() || null : undefined,
      }),
    onSuccess: () => {
      setValue('');
      setLabel('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ADMIN_OPTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY });
      onAdded();
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to add option';
      setError(typeof detail === 'string' ? detail : 'Failed to add option');
    },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label={`New ${CATEGORY_LABELS[category].toLowerCase()} value`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{ ...fieldSx, flex: 1, minWidth: 220 }}
          disabled={createMutation.isPending}
        />
        {category === 'industry' && (
          <TextField
            size="small"
            label="Short label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ ...fieldSx, width: 140 }}
            disabled={createMutation.isPending}
          />
        )}
        <Button
          variant="contained"
          startIcon={
            createMutation.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          disabled={!value.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: STORY_UI.radiusSm,
            backgroundColor: STORY_UI.accent,
            '&:hover': { backgroundColor: STORY_UI.accentDeep },
          }}
        >
          Add
        </Button>
      </Box>
      {error && <Typography sx={{ fontSize: '12px', color: '#c00000' }}>{error}</Typography>}
    </Box>
  );
};

export const OptionsManagerPanel: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<SolutionStoryOptionCategory>('practice');
  const [search, setSearch] = useState('');
  const [missingIconOnly, setMissingIconOnly] = useState(false);

  const optionsQuery = useQuery({
    queryKey: ADMIN_OPTIONS_QUERY_KEY,
    queryFn: () => solutionStoriesApi.listAdminOptions(undefined, false),
    staleTime: 60 * 1000,
  });

  if (optionsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: STORY_UI.accent }} />
      </Box>
    );
  }

  if (optionsQuery.error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Failed to load options. Please refresh and try again.
      </Alert>
    );
  }

  const options = optionsQuery.data ?? [];
  const isCompanyTab = activeCategory === 'company';
  const activeRows = options
    .filter((o) => o.category === activeCategory)
    .filter((o) => !search.trim() || o.value.toLowerCase().includes(search.trim().toLowerCase()))
    .filter((o) => !(isCompanyTab && missingIconOnly) || !o.icon_url)
    .sort((a, b) => a.sort_order - b.sort_order || a.value.localeCompare(b.value));

  const countFor = (category: SolutionStoryOptionCategory) =>
    options.filter((o) => o.category === category && o.is_active).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Renaming a value does not update stories already saved with the old value. Deleting an
        option only hides it (existing stories keep rendering).
      </Alert>

      <Tabs
        value={activeCategory}
        onChange={(_e, v: SolutionStoryOptionCategory) => setActiveCategory(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: `1px solid ${STORY_UI.line}`,
          minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontFamily: STORY_UI.fontSans,
            fontWeight: 600,
            fontSize: '13.5px',
            color: STORY_UI.inkSoft,
            minHeight: 40,
          },
          '& .Mui-selected': { color: `${STORY_UI.accent} !important` },
          '& .MuiTabs-indicator': { backgroundColor: STORY_UI.accent, height: 3 },
        }}
      >
        {CATEGORY_ORDER.map((category) => (
          <Tab
            key={category}
            value={category}
            label={`${CATEGORY_LABELS[category]} (${countFor(category)})`}
          />
        ))}
      </Tabs>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: { xs: 2, sm: 2.5 },
          borderRadius: STORY_UI.radiusMd,
          border: `1px solid ${STORY_UI.line}`,
          backgroundColor: STORY_UI.surface,
        }}
      >
        {isCompanyTab && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search company name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: STORY_UI.inkFaint }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={missingIconOnly}
                  onChange={(e) => setMissingIconOnly(e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: '13px' }}>Missing logo only</Typography>}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {activeRows.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: STORY_UI.inkFaint }}>
              No options {search.trim() || missingIconOnly ? 'match this filter' : 'yet'}.
            </Typography>
          ) : (
            activeRows.map((option, idx) => (
              <OptionRow
                key={option.id}
                option={option}
                isFirst={idx === 0}
                isLast={idx === activeRows.length - 1}
                onChanged={() => optionsQuery.refetch()}
              />
            ))
          )}
        </Box>

        <AddOptionRow category={activeCategory} onAdded={() => optionsQuery.refetch()} />
      </Box>
    </Box>
  );
};
